'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Section, Container } from '@layout';
import { Card, Heading, Input } from '@ui';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface SearchChipsDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  /** initial uncontrolled tokens */
  initialTokens?: string[];
  /** optional recent suggestions to show on focus when the input is empty */
  recent?: string[];
  /** controlled tokens (optional) */
  tokens?: string[];
  /** callback when token list changes */
  onTokensChange?: (tokens: string[]) => void;
  cardClassName?: string;
  isSpanish?: boolean;
}

const SearchChipsDemo: React.FC<SearchChipsDemoProps> = ({
  isSpanish = false,
  sectionID = 'searchchips',
  classes = '',
  containerId,
  containerClasses,
  initialTokens,
  tokens: tokensProp,
  onTokensChange,
  recent,
  cardClassName,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  const SUGGEST = useMemo(() => {
    const en = [
      'AWS',
      'Component systems',
      'Next.js',
      'React',
      'TypeScript',
      'Web Development',
      'Web Development Management',
    ];

    const es = [
      'AWS',
      'Sistemas de componentes',
      'Next.js',
      'React',
      'TypeScript',
      'Desarrollo web',
      'Gestion de desarrollo web',
    ];

    return isSpanish ? es : en;
  }, [isSpanish]);

  const [tokenSet, setTokenSet] = useState<Set<string>>(new Set());
  useEffect(() => {
    setTokenSet(new Set(initialTokens ?? tokensProp ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [query, setQuery] = useState('');
  const [filtered, setFiltered] = useState<string[]>([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (!announcement) return;
    const t = setTimeout(() => setAnnouncement(''), 1500);
    return () => clearTimeout(t);
  }, [announcement]);

  const computeFiltered = useCallback(
    (q?: string) => {
      const v = (q ?? query).trim().toLowerCase();
      const ts = new Set(tokenSet as Set<string>);
      let res: string[] = [];
      if (v) {
        res = SUGGEST.filter((x) => x.toLowerCase().includes(v) && !ts.has(x));
      } else if (recent && recent.length) {
        res = recent.filter((x) => !ts.has(x));
      } else {
        res = SUGGEST.filter((x) => !ts.has(x));
      }
      setFiltered(res);
      setActive(0);
    },
    [query, SUGGEST, tokenSet, recent]
  );

  useEffect(() => {
    computeFiltered();
  }, [computeFiltered]);

  const isControlled = tokensProp !== undefined;
  useEffect(() => {
    if (isControlled && tokensProp) setTokenSet(new Set(tokensProp));
  }, [isControlled, tokensProp]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  function addToken(val: string) {
    const v = String(val).trim();
    if (!v) return;
    setTokenSet((prev) => {
      const next = new Set(prev);
      next.add(v);
      const arr = Array.from(next);
      if (onTokensChange) onTokensChange(arr);
      setAnnouncement(isSpanish ? `Agregado ${v}` : `Added ${v}`);
      return next;
    });
    setQuery('');
    setOpen(false);
  }

  function removeToken(val: string) {
    setTokenSet((prev) => {
      const next = new Set(prev);
      next.delete(val);
      const arr = Array.from(next);
      if (onTokensChange) onTokensChange(arr);
      setAnnouncement(isSpanish ? `Eliminado ${val}` : `Removed ${val}`);
      return next;
    });
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const raw = query.replace(/,$/, '').trim();
      const hasQuery = raw.length > 0;
      if (filtered.length > 0 && (hasQuery || active > 0)) {
        const idx = ((active % filtered.length) + filtered.length) % filtered.length;
        addToken(filtered[idx] ?? '');
      } else if (hasQuery) {
        addToken(raw);
      }
      setQuery('');
      return;
    }
    if (e.key === 'Backspace' && query === '' && tokenSet.size) {
      const last = Array.from(tokenSet).pop();
      if (last) removeToken(last);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (filtered.length ? (a + 1) % filtered.length : 0));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (filtered.length ? (a - 1 + filtered.length) % filtered.length : 0));
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  function onListClick(val: string) {
    addToken(val);
    inputRef.current?.focus();
  }

  const chips = Array.from(tokenSet) as string[];

  return (
    <Section sectionID={sectionID} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading
          as='h3'
          title={t('Tag chips with typeahead', 'Chips con autocompletado')}
          description={t(
            'The suggestion list filters as you type (for example, typing access narrows the list toward Accessibility Standards). Pick an item with the mouse, or Arrow keys then Enter or comma. If your text matches nothing in the list, Enter or comma adds a custom tag. When the field is empty, Backspace removes the last chip. With an empty field, focusing shows either recent picks (when configured) or the full suggestion list.',
            'La lista se filtra al escribir (por ejemplo, acces deja en la lista opciones como Normas de accesibilidad). Elige con el raton, o flechas y Enter o coma. Si no hay coincidencias, Enter o coma agrega una etiqueta libre. Con el campo vacio, Retroceso quita el ultimo chip. Al enfocar con el campo vacio se muestran recientes (si hay) o todas las sugerencias.'
          )}
        />

        <Card className={cardClassName}>
          <div
            className={['chip-input', open ? 'open' : ''].filter(Boolean).join(' ')}
            data-chip-input
            id='chips1'
            ref={rootRef}
          >
            <label className='hint' htmlFor='chipEditor1'>
              {t('Query', 'Consulta')}
            </label>

            <div
              className={`chip-box ${chips.length === 0 ? 'chip-box--empty' : ''}`}
              onClick={(e) => {
                const target = e.target as HTMLElement | null;
                if (target && target.closest && target.closest('button')) return;
                computeFiltered(inputRef.current?.value ?? query);
                setOpen(true);
                inputRef.current?.focus();
              }}
            >
              <div
                className='chip-holder'
                role='group'
                aria-label={t('Selected filters', 'Filtros seleccionados')}
              >
                {chips.map((c) => (
                  <span key={c} className='chip'>
                    {c}
                    <button
                      aria-label={isSpanish ? `Eliminar ${c}` : `Remove ${c}`}
                      data-rm={c}
                      onClick={() => removeToken(c)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div aria-live='polite' className='sr-only' role='status'>
                {announcement}
              </div>
              <Input
                id='chipEditor1'
                ref={inputRef}
                className='chip-editor'
                autoComplete='off'
                role='combobox'
                aria-controls='chipSuggest1'
                aria-expanded={open}
                aria-haspopup='listbox'
                aria-autocomplete='list'
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => {
                  computeFiltered(inputRef.current?.value ?? query);
                  setOpen(true);
                }}
                onKeyDown={onInputKeyDown}
                placeholder={t(
                  'Type to filter the list, then Enter or comma…',
                  'Escribe para filtrar la lista; luego Enter o coma…'
                )}
              />
            </div>

            <ul className='chip-suggest' id='chipSuggest1' role='listbox'>
              {open &&
                filtered.map((v, i) => (
                  <li
                    key={v}
                    className={`chip-opt ${i === active ? 'active' : ''}`}
                    role='option'
                    aria-selected={i === active}
                    data-val={v}
                    onMouseDown={(ev) => ev.preventDefault()} /* prevent blur */
                    onClick={() => onListClick(v)}
                  >
                    {v}
                  </li>
                ))}
            </ul>
            <small className='hint'>
              {t(
                'Examples: React, TypeScript, Next.js, AWS, Web Development…',
                'Ejemplos: React, TypeScript, Next.js, AWS, desarrollo web…'
              )}
            </small>
          </div>
        </Card>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="chip-input">
  <label class="hint" for="skills">Skills</label>
  <div class="chip-box">
    <div class="chip-holder">
      <span class="chip">React <button type="button" aria-label="Remove React">×</button></span>
    </div>
    <input id="skills" class="chip-editor" type="text" />
  </div>
  <ul class="chip-suggest" role="listbox">
    <li class="chip-opt" role="option">TypeScript</li>
  </ul>
</div>`}
        />
      </Container>
    </Section>
  );
};

export default SearchChipsDemo;
